/**
 * segment-gesture.helper.js
 *
 * Matemática PURA del gesto de avance INLINE sobre una polilínea.
 * Replica la proyección punto→arco que RegisterAdvanceModal hace en coords SVG
 * (_ProjectToMeters / metersSeq / metersCum), pero operando directamente sobre
 * los vértices de la entidad en coords MUNDO (unidades DXF, tratadas como metros).
 *
 * Sin THREE, sin Vue, sin estado de componente. El llamador es responsable de
 * pasar `point` y `vertices` en el MISMO marco de coordenadas (p. ej. ambos ya
 * restado el origen del DXF, como hacen los hitboxes de DxfInteractiveOverlay).
 *
 * Convención de dirección (idéntica al modal):
 *   'l2r' → recorre los vértices tal cual (inicio = extremo A = vertices[0]).
 *   'r2l' → recorre la secuencia invertida (inicio = extremo B = último vértice).
 */

/** Normaliza un punto {x,y} (acepta THREE.Vector3, que también expone x,y). */
function toXY(p) {
  return { x: (p && p.x) || 0, y: (p && p.y) || 0 }
}

/**
 * Secuencia de vértices en el orden de avance (invertida si direction='r2l').
 * @param {Array<{x:number,y:number}>} vertices
 * @param {'l2r'|'r2l'} direction
 * @returns {Array<{x:number,y:number}>}
 */
export function orderedVertices(vertices, direction) {
  const pts = (vertices || []).map(toXY)
  if (pts.length < 2) return pts
  return direction === 'r2l' ? pts.slice().reverse() : pts
}

/**
 * Longitudes acumuladas a lo largo de una secuencia ya ordenada.
 * @param {Array<{x:number,y:number}>} seq
 * @returns {{ cum: number[], total: number }}
 */
export function cumulativeLengths(seq) {
  const cum = [0]
  for (let i = 1; i < seq.length; i++) {
    const dx = seq[i].x - seq[i - 1].x
    const dy = seq[i].y - seq[i - 1].y
    cum.push(cum[i - 1] + Math.hypot(dx, dy))
  }
  return { cum, total: cum[cum.length - 1] || 0 }
}

/**
 * Extremo de la polilínea más cercano al punto → dirección de inicio.
 * Elige entre el primer vértice (A → 'l2r') y el último (B → 'r2l').
 * @param {{x:number,y:number}} point
 * @param {Array<{x:number,y:number}>} vertices
 * @returns {'l2r'|'r2l'}
 */
export function nearestEnd(point, vertices) {
  const pts = (vertices || []).map(toXY)
  if (pts.length < 2) return 'l2r'
  const p = toXY(point)
  const a = pts[0]
  const b = pts[pts.length - 1]
  const dA = (p.x - a.x) ** 2 + (p.y - a.y) ** 2
  const dB = (p.x - b.x) ** 2 + (p.y - b.y) ** 2
  return dA <= dB ? 'l2r' : 'r2l'
}

/**
 * Proyecta un punto sobre la polilínea (en el sentido `direction`) y devuelve la
 * fracción de arco recorrida [0..1]. Mismo algoritmo que el modal: para cada
 * segmento se calcula la proyección perpendicular clampada y se toma el segmento
 * más cercano, acumulando su longitud de arco.
 * @param {{x:number,y:number}} point
 * @param {Array<{x:number,y:number}>} vertices
 * @param {'l2r'|'r2l'} direction
 * @returns {number} fracción 0..1 (0 si la polilínea es degenerada)
 */
export function projectPointToArcFrac(point, vertices, direction) {
  const seq = orderedVertices(vertices, direction)
  const { cum, total } = cumulativeLengths(seq)
  if (seq.length < 2 || total <= 0) return 0
  const p = toXY(point)
  let best = { d: Infinity, arc: 0 }
  for (let i = 1; i < seq.length; i++) {
    const a = seq[i - 1]
    const b = seq[i]
    const abx = b.x - a.x
    const aby = b.y - a.y
    const len2 = abx * abx + aby * aby || 1e-9
    let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2
    t = Math.max(0, Math.min(t, 1))
    const px = a.x + abx * t
    const py = a.y + aby * t
    const d = (p.x - px) * (p.x - px) + (p.y - py) * (p.y - py)
    if (d < best.d) best = { d, arc: cum[i - 1] + t * Math.hypot(abx, aby) }
  }
  return Math.max(0, Math.min(best.arc / total, 1))
}

/** Fracción de arco [0..1] → metros sobre la longitud total. */
export function arcFracToMeters(frac, totalLength) {
  const f = Math.max(0, Math.min(frac || 0, 1))
  return f * (totalLength || 0)
}

/** Metros → fracción de arco [0..1] (clamp). */
export function metersToArcFrac(meters, totalLength) {
  if (!totalLength || totalLength <= 0) return 0
  return Math.max(0, Math.min((meters || 0) / totalLength, 1))
}

/**
 * Atajo: proyecta un punto y devuelve directamente los metros recorridos,
 * clampados a [0, totalLength]. Combina projectPointToArcFrac + arcFracToMeters.
 * @returns {number}
 */
export function projectPointToMeters(point, vertices, direction, totalLength) {
  const frac = projectPointToArcFrac(point, vertices, direction)
  return arcFracToMeters(frac, totalLength)
}

/**
 * Coordenada mundo {x,y} a una fracción de arco dada (en el sentido `direction`).
 * Útil para ubicar el handle/chip de metros sobre el trazo durante el gesto.
 * @returns {{x:number,y:number,i:number}} punto + índice de segmento alcanzado
 */
export function pointAtArcFrac(frac, vertices, direction) {
  const seq = orderedVertices(vertices, direction)
  const { cum, total } = cumulativeLengths(seq)
  if (seq.length < 2 || total <= 0) {
    const first = seq[0] || { x: 0, y: 0 }
    return { x: first.x, y: first.y, i: 0 }
  }
  const arc = Math.max(0, Math.min(frac || 0, 1)) * total
  for (let i = 1; i < seq.length; i++) {
    if (cum[i] >= arc) {
      const segLen = cum[i] - cum[i - 1] || 1e-9
      const t = Math.max(0, Math.min((arc - cum[i - 1]) / segLen, 1))
      const a = seq[i - 1]
      const b = seq[i]
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, i }
    }
  }
  const last = seq[seq.length - 1]
  return { x: last.x, y: last.y, i: seq.length - 1 }
}
